import React, { useEffect, useMemo, useState } from 'react';
import { collection, collectionGroup, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { db } from './../../../firebase.js';
import { getCategoryColor } from './../../../common/categoryColors.js';
import { getAdminEvalGroupLabel } from './../../../common/adminEvalLabel.js';
import './WeeklyUsagePage.css';

function WeeklyUsagePage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [displayUnitRanges, setDisplayUnitRanges] = useState([]);
    const [inferenceRecords, setInferenceRecords] = useState([]);

    const getLastSevenIsoDays = () => {
        const now = new Date();
        const todayUtc = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
        const days = [];
        for (let offset = 6; offset >= 0; offset -= 1) {
            const day = new Date(todayUtc);
            day.setUTCDate(todayUtc.getUTCDate() - offset);
            days.push(day.toISOString().split('T')[0]);
        }
        return days;
    };

    const getIsoDayFromTimestamp = (timestampMs) => {
        const date = new Date(Number(timestampMs));
        if (Number.isNaN(date.getTime())) {
            return null;
        }
        const utcDate = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
        return utcDate.toISOString().split('T')[0];
    };

    const getWeekdayLabelFromIsoDay = (isoDay) => {
        const date = new Date(`${isoDay}T00:00:00Z`);
        if (Number.isNaN(date.getTime())) return isoDay;
        const weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
        return weekdays[(date.getUTCDay() + 6) % 7];
    };

    const hexToRgb = (hex) => {
        const bigint = Number.parseInt(hex.slice(1), 16);
        return {
            r: (bigint >> 16) & 255,
            g: (bigint >> 8) & 255,
            b: bigint & 255
        };
    };

    const getCellBackgroundColor = (baseColor, count, maxCount) => {
        if (count <= 0 || maxCount <= 0) return 'rgba(255, 255, 255, 0.1)';
        const colorObj = hexToRgb(baseColor);
        const opacity = Math.max(count / maxCount, 0.15);
        return `rgba(${colorObj.r},${colorObj.g},${colorObj.b},${opacity})`;
    };

    const extractEvalLabel = (evalValue) => getAdminEvalGroupLabel(evalValue) || 'UNDEFINED';

    useEffect(() => {
        const loadWeeklyUsage = async () => {
            try {
                const sevenIsoDays = getLastSevenIsoDays();
                const startMs = new Date(`${sevenIsoDays[0]}T00:00:00Z`).getTime();
                const [unitsSnapshot, locationsSnapshot, excludedSnapshot] = await Promise.all([
                    getDocs(collection(db, 'units')),
                    getDocs(collectionGroup(db, 'locations')),
                    getDoc(doc(db, 'unit_filter_environments', 'excluded_from_statistics'))
                ]);
                const excludedData = excludedSnapshot.exists() ? (excludedSnapshot.data() || {}) : {};
                const excludedUnitIds = new Set(
                    (Array.isArray(excludedData.units) ? excludedData.units : []).map((unitId) => String(unitId))
                );

                const unitRecordsPromises = [];
                const nextDisplayUnitRanges = [];

                unitsSnapshot.forEach((unitDoc) => {
                    const unit = unitDoc.data() || {};
                    const unitId = unit.unit_id || unitDoc.id;
                    if (!unitId) return;
                    if (excludedUnitIds.has(String(unitId))) return;
                    const recordsQuery = query(
                        collection(db, 'units', unitId, 'inference-records'),
                        where('timestamp', '>=', startMs)
                    );
                    unitRecordsPromises.push(
                        getDocs(recordsQuery).then((recordsSnapshot) => recordsSnapshot.docs.map((recordDoc) => {
                            const data = recordDoc.data() || {};
                            return {
                                unitId,
                                timestamp: Number(data.timestamp),
                                evalValue: data.eval,
                                isTest: data.is_test === true
                            };
                        }))
                    );
                });

                locationsSnapshot.forEach((locationDoc) => {
                    const locationData = locationDoc.data() || {};
                    const displayUnits = Array.isArray(locationData.display_units) ? locationData.display_units : [];

                    displayUnits.forEach((displayUnit) => {
                        const category =
                            displayUnit?.deployment_config?.category ||
                            displayUnit?.deployment_config?.classification?.category;
                        if (typeof category !== 'string' || category.trim().length === 0) return;
                        const unitRanges = Array.isArray(displayUnit?.unit_ranges) ? displayUnit.unit_ranges : [];
                        unitRanges.forEach((range) => {
                            if (!range?.unit_id) return;
                            const startDate = Number(range.start_date);
                            const endDate = Number(range.end_date);
                            nextDisplayUnitRanges.push({
                                unitId: String(range.unit_id),
                                category,
                                current: range.current === true,
                                startDate: Number.isFinite(startDate) ? startDate : null,
                                endDate: Number.isFinite(endDate) ? endDate : null
                            });
                        });
                    });
                });

                const recordsArrays = await Promise.all(unitRecordsPromises);
                const combinedRecords = recordsArrays
                    .flat()
                    .filter((record) => !record.isTest && Number.isFinite(record.timestamp) && !excludedUnitIds.has(String(record.unitId)));

                setDisplayUnitRanges(nextDisplayUnitRanges);
                setInferenceRecords(combinedRecords);
            } catch (error) {
                console.log('Error calculating weekly usage data on page open:', error);
                setDisplayUnitRanges([]);
                setInferenceRecords([]);
            }
        };

        loadWeeklyUsage();
    }, []);

    const weekIsoDays = useMemo(() => getLastSevenIsoDays(), []);

    const sections = useMemo(() => {
        const daySet = new Set(weekIsoDays);
        const usageByCategory = {};

        const getDisplayUnitCategoryForRecord = (record) => {
            const timestamp = Number(record.timestamp);
            if (!Number.isFinite(timestamp)) return null;

            const matchingRanges = displayUnitRanges.filter((range) => {
                if (range.unitId !== String(record.unitId)) return false;
                if (range.current) {
                    return range.startDate === null || timestamp >= range.startDate;
                }
                if (range.startDate === null || range.endDate === null) return false;
                return timestamp >= range.startDate && timestamp <= range.endDate;
            });

            if (matchingRanges.length === 0) return null;

            matchingRanges.sort((a, b) => {
                const aStart = a.startDate ?? -Infinity;
                const bStart = b.startDate ?? -Infinity;
                return bStart - aStart;
            });

            return matchingRanges[0].category;
        };

        const ensureCell = (category, evalLabel, day) => {
            usageByCategory[category] = usageByCategory[category] || {};
            usageByCategory[category][evalLabel] = usageByCategory[category][evalLabel] || {};
            usageByCategory[category][evalLabel][day] = (usageByCategory[category][evalLabel][day] || 0) + 1;
        };

        inferenceRecords.forEach((record) => {
            const day = getIsoDayFromTimestamp(record.timestamp);
            if (!daySet.has(day)) return;
            const category = getDisplayUnitCategoryForRecord(record);
            if (!category) return;
            const evalLabel = extractEvalLabel(record.evalValue);
            ensureCell(category, evalLabel, day);
        });

        const categoryNames = Object.keys(usageByCategory).sort((a, b) => a.localeCompare(b));
        return categoryNames.map((category, categoryIndex) => {
            const rows = Object.keys(usageByCategory[category])
                .sort((a, b) => a.localeCompare(b))
                .map((evalLabel) => {
                    const usage = weekIsoDays.map((day) => Number(usageByCategory[category][evalLabel][day] || 0));
                    return { evalLabel, usage };
                });
            return {
                category,
                rows,
                color: getCategoryColor(category, categoryIndex + 1)
            };
        });
    }, [weekIsoDays, inferenceRecords, displayUnitRanges]);

    const leftSections = useMemo(
        () => sections.filter((_, index) => index % 2 === 0),
        [sections]
    );
    const rightSections = useMemo(
        () => sections.filter((_, index) => index % 2 === 1),
        [sections]
    );

    return (
        <div className="weekly-usage-page">
            <div className="weekly-usage-actions">
                <button
                    className="weekly-usage-action-button"
                    onClick={() => navigate('/admin/weekly-usage/units')}
                >
                    {t('View by unit')}
                </button>
            </div>
            <div className="weekly-usage-columns">
                <div className="weekly-usage-column">
                    {leftSections.map((section) => (
                        <div key={section.category} className="weekly-category-card">
                            <div className="weekly-category-title" style={{ color: section.color }}>
                                {t(section.category)}
                            </div>
                            <div className="weekly-day-labels">
                                {weekIsoDays.map((isoDay, index) => (
                                    <div key={index} className="weekly-day-label">
                                        {t(getWeekdayLabelFromIsoDay(isoDay))}
                                    </div>
                                ))}
                            </div>
                            <div className="weekly-rows">
                                {section.rows.map((row) => {
                                    const maxValue = Math.max(...row.usage, 0);
                                    return (
                                        <div key={row.evalLabel} className="weekly-row">
                                            <div className="weekly-row-label">{t(row.evalLabel)}</div>
                                            <div className="weekly-values">
                                                {row.usage.map((value, idx) => (
                                                    <div
                                                        key={idx}
                                                        className="weekly-cell"
                                                            style={{ background: getCellBackgroundColor(section.color, value, maxValue) }}
                                                    >
                                                        {value}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
                <div className="weekly-usage-column">
                    {rightSections.map((section) => (
                        <div key={section.category} className="weekly-category-card">
                            <div className="weekly-category-title" style={{ color: section.color }}>
                                {t(section.category)}
                            </div>
                            <div className="weekly-day-labels">
                                {weekIsoDays.map((isoDay, index) => (
                                    <div key={index} className="weekly-day-label">
                                        {t(getWeekdayLabelFromIsoDay(isoDay))}
                                    </div>
                                ))}
                            </div>
                            <div className="weekly-rows">
                                {section.rows.map((row) => {
                                    const maxValue = Math.max(...row.usage, 0);
                                    return (
                                        <div key={row.evalLabel} className="weekly-row">
                                            <div className="weekly-row-label">{t(row.evalLabel)}</div>
                                            <div className="weekly-values">
                                                {row.usage.map((value, idx) => (
                                                    <div
                                                        key={idx}
                                                        className="weekly-cell"
                                                            style={{ background: getCellBackgroundColor(section.color, value, maxValue) }}
                                                    >
                                                        {value}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default WeeklyUsagePage;
